import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import LoadingSpinner from "./LoadingSpinner";
import { ErrorBox } from "./ErrorBox";
import { type APIError } from "@/api/APIError";

type DialogVariant = "default" | "danger" | "warning";

type DialogProps = {
    title: string;
    description?: string;
    children?: React.ReactNode;
    error?: APIError | null;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: DialogVariant;
    loading?: boolean;
    onConfirm: () => void;
    onClose: () => void;
};

const variantStyles: Record<DialogVariant, { confirm: string; icon: string }> = {
    default: {
        confirm: "bg-blue text-white hover:bg-dark-blue disabled:bg-light-grey",
        icon: "",
    },
    danger: {
        confirm: "bg-error-red text-white hover:bg-dark-red disabled:bg-light-grey",
        icon: "text-error-red",
    },
    warning: {
        confirm: "bg-dark-orange text-white hover:bg-darker-orange disabled:bg-light-grey",
        icon: "text-dark-orange",
    },
};

export function Dialog({
    title,
    description,
    children,
    error,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    variant = "default",
    loading = false,
    onConfirm,
    onClose,
}: DialogProps) {
    const styles = variantStyles[variant];
    const confirmRef = useRef<HTMLButtonElement>(null);
    const cancelRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const prev = document.activeElement as HTMLElement | null;
        cancelRef.current?.focus();
        return () => { prev?.focus(); };
    }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !loading) onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [loading, onClose]);

    return createPortal(
        <div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-[1px]"
            onClick={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}
            role="dialog"
            aria-modal
            aria-labelledby="dialog-title"
        >
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
                {/* Header */}
                <div className="flex items-start justify-between px-6 pt-6 pb-4">
                    <h2 id="dialog-title" className={`text-base font-semibold ${styles.icon || "text-dark-dark-grey"}`}>
                        {title}
                    </h2>
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="text-light-grey hover:text-grey transition-colors disabled:opacity-50 -mt-0.5 -mr-1"
                    >
                        <CloseIcon fontSize="small" />
                    </button>
                </div>

                {/* Body */}
                {(description || children) && (
                    <div className="px-6 pb-6 flex flex-col gap-4">
                        {description && (
                            <p className="text-sm text-grey leading-relaxed">{description}</p>
                        )}
                        {children}
                    </div>
                )}

                <ErrorBox error={error} className="mx-6 mb-6" />

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 bg-desaturated-grey border-t border-light-light-grey">
                    <button
                        ref={cancelRef}
                        onClick={onClose}
                        disabled={loading}
                        className="h-10 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center disabled:cursor-not-allowed bg-white text-grey border-2 border-light-light-grey hover:text-dark-grey disabled:opacity-50"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        ref={confirmRef}
                        onClick={onConfirm}
                        disabled={loading}
                        className={`h-10 px-4 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 justify-center disabled:cursor-not-allowed ${styles.confirm}`}
                    >
                        {loading && <LoadingSpinner width={14} height={14} color="white" secondaryColor="rgba(255,255,255,0.3)" />}
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
